/**
 * Mock Data Service for Development
 * Provides realistic test data when API is unavailable or rate limited
 */

export const mockDashboardMetrics = {
  total_agents: 184,
  active_agents: 176,
  status: 'operational',
  total_scans_today: 47342,
  monthly_profit: 284750,
  products_analyzed: 18947,
  system_uptime: 99.2,
  top_products: [
    {
      id: 'p1',
      name: 'Wireless Bluetooth Headphones',
      price: 89.99,
      marketplace: 'Amazon',
      performance: 94.5,
      scans: 1247
    },
    {
      id: 'p2',
      name: 'Smart Fitness Watch',
      price: 199.99,
      marketplace: 'Best Buy',
      performance: 91.2,
      scans: 982
    },
    {
      id: 'p3',
      name: 'Laptop Stand Adjustable',
      price: 34.99,
      marketplace: 'Walmart',
      performance: 88.7,
      scans: 756
    },
    {
      id: 'p4',
      name: 'USB-C Fast Charger',
      price: 24.99,
      marketplace: 'Target',
      performance: 86.3,
      scans: 654
    },
    {
      id: 'p5',
      name: 'Wireless Gaming Mouse',
      price: 79.99,
      marketplace: 'Newegg',
      performance: 84.1,
      scans: 543
    }
  ],
  revenue_trend: [
    { date: '2025-09-13', revenue: 127500 },
    { date: '2025-09-14', revenue: 134200 },
    { date: '2025-09-15', revenue: 129800 },
    { date: '2025-09-16', revenue: 142100 },
    { date: '2025-09-17', revenue: 138900 },
    { date: '2025-09-18', revenue: 151200 },
    { date: '2025-09-19', revenue: 148600 }
  ]
};

export const mockAgentsStatus = {
  total_agents: 184,
  active_agents: 176,
  inactive_agents: 8,
  agents: [
    {
      id: 'agent-001',
      name: 'Amazon Price Monitor',
      status: 'active',
      marketplace: 'Amazon',
      last_run: '2025-09-19T14:10:32Z',
      accuracy: 98.5,
      products_monitored: 1247
    },
    {
      id: 'agent-002',
      name: 'eBay Competition Tracker',
      status: 'active',
      marketplace: 'eBay',
      last_run: '2025-09-19T14:09:45Z',
      accuracy: 96.3,
      products_monitored: 892
    },
    {
      id: 'agent-003',
      name: 'Walmart Inventory Checker',
      status: 'active',
      marketplace: 'Walmart',
      last_run: '2025-09-19T14:11:02Z',
      accuracy: 97.8,
      products_monitored: 1056
    },
    {
      id: 'agent-004',
      name: 'Best Buy Stock Monitor',
      status: 'maintenance',
      marketplace: 'Best Buy',
      last_run: '2025-09-19T13:45:12Z',
      accuracy: 94.2,
      products_monitored: 634
    },
    {
      id: 'agent-005',
      name: 'Target Price Sync',
      status: 'active',
      marketplace: 'Target',
      last_run: '2025-09-19T14:08:17Z',
      accuracy: 99.1,
      products_monitored: 743
    }
  ]
};

export const mockScrapersStatus = {
  total_scrapers: 83,
  active_scrapers: 79,
  failed_scrapers: 4,
  scrapers: [
    {
      id: 'scraper-001',
      name: 'Amazon Product Scraper',
      marketplace: 'Amazon',
      status: 'active',
      last_run: '2025-09-19T14:12:15Z',
      accuracy: 99.2,
      items_scraped: 15420,
      avg_response_time: 245
    },
    {
      id: 'scraper-002',
      name: 'eBay Listings Scraper',
      marketplace: 'eBay',
      status: 'active',
      last_run: '2025-09-19T14:11:48Z',
      accuracy: 97.8,
      items_scraped: 12680,
      avg_response_time: 312
    },
    {
      id: 'scraper-003',
      name: 'Walmart Catalog Scraper',
      marketplace: 'Walmart',
      status: 'active',
      last_run: '2025-09-19T14:10:33Z',
      accuracy: 98.5,
      items_scraped: 18240,
      avg_response_time: 198
    },
    {
      id: 'scraper-004',
      name: 'Shopify Store Monitor',
      marketplace: 'Shopify',
      status: 'idle',
      last_run: '2025-09-19T13:55:22Z',
      accuracy: 89.3,
      items_scraped: 8920,
      avg_response_time: 567,
      error: 'Connection timeout'
    }
  ]
};

export const mockRecentAlerts = [
  {
    id: 'alert-001',
    type: 'price_drop',
    level: 'info',
    product: 'Wireless Bluetooth Headphones',
    message: 'Price dropped by 15% on Amazon',
    timestamp: '2025-09-19T14:05:32Z',
    marketplace: 'Amazon'
  },
  {
    id: 'alert-002',
    type: 'low_stock',
    level: 'warning',
    product: 'Smart Fitness Watch',
    message: 'Only 3 units left in stock',
    timestamp: '2025-09-19T13:58:47Z',
    marketplace: 'Best Buy'
  },
  {
    id: 'alert-003',
    type: 'new_competitor',
    level: 'info',
    product: 'USB-C Fast Charger',
    message: 'New seller offering similar product at lower price',
    timestamp: '2025-09-19T13:42:18Z',
    marketplace: 'eBay'
  }
];

export const mockProductSearch = (query = '', limit = 20) => {
  const allProducts = [
    {
      id: 'p1',
      name: 'Wireless Bluetooth Headphones Premium',
      description: 'High-quality wireless headphones with noise cancellation',
      price: 89.99,
      marketplace: 'Amazon',
      image: '/api/placeholder/400/300',
      category: 'Electronics',
      brand: 'TechSound',
      rating: 4.5,
      reviews: 1247
    },
    {
      id: 'p2',
      name: 'Smart Fitness Watch Pro',
      description: 'Advanced fitness tracking with heart rate monitor',
      price: 199.99,
      marketplace: 'Best Buy',
      image: '/api/placeholder/400/300',
      category: 'Wearables',
      brand: 'FitTech',
      rating: 4.3,
      reviews: 892
    },
    {
      id: 'p3',
      name: 'Ergonomic Laptop Stand Adjustable',
      description: 'Aluminum laptop stand with adjustable height',
      price: 34.99,
      marketplace: 'Walmart',
      image: '/api/placeholder/400/300',
      category: 'Office',
      brand: 'DeskPro',
      rating: 4.7,
      reviews: 756
    },
    {
      id: 'p4',
      name: 'USB-C Fast Charger 65W',
      description: 'Quick charge adapter with USB-C cable',
      price: 24.99,
      marketplace: 'Target',
      image: '/api/placeholder/400/300',
      category: 'Electronics',
      brand: 'PowerMax',
      rating: 4.2,
      reviews: 654
    },
    {
      id: 'p5',
      name: 'Wireless Gaming Mouse RGB',
      description: 'High-precision gaming mouse with RGB lighting',
      price: 79.99,
      marketplace: 'Newegg',
      image: '/api/placeholder/400/300',
      category: 'Gaming',
      brand: 'GamePro',
      rating: 4.6,
      reviews: 543
    }
  ];

  const filtered = query
    ? allProducts.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.category.toLowerCase().includes(query.toLowerCase()) ||
        p.brand.toLowerCase().includes(query.toLowerCase())
      )
    : allProducts;

  return {
    products: filtered.slice(0, limit),
    total: filtered.length,
    query,
    limit
  };
};

/**
 * Mock service that mimics the real API client
 */
export const mockApiService = {
  health: async () => ({ status: 'healthy', timestamp: new Date().toISOString() }),

  metrics: async () => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockDashboardMetrics;
  },

  alerts: {
    recent: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return mockRecentAlerts;
    }
  },

  agents: {
    status: async () => {
      await new Promise(resolve => setTimeout(resolve, 250));
      return mockAgentsStatus;
    }
  },

  scrapers: {
    list: async () => {
      await new Promise(resolve => setTimeout(resolve, 280));
      return mockScrapersStatus;
    }
  },

  products: {
    search: async (query, options = {}) => {
      await new Promise(resolve => setTimeout(resolve, 150));
      return mockProductSearch(query, options.limit);
    },

    barcodeScan: async (payload) => {
      await new Promise(resolve => setTimeout(resolve, 400));
      // Simulate barcode scan result
      return {
        product: {
          id: 'scan-001',
          barcode: typeof payload === 'string' ? payload : payload.barcode,
          name: 'Scanned Product',
          price: 29.99,
          marketplace: 'Amazon'
        }
      };
    }
  }
};

/**
 * Check if we should use mock data (rate limited or development mode)
 */
export function shouldUseMockData(error) {
  const fallbackEnabled = import.meta.env.VITE_FALLBACK_TO_MOCK === 'true';
  const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === 'development';

  if (!fallbackEnabled && !isDevelopment) {
    return false;
  }

  return error?.status === 429 || isDevelopment;
}
